// ManufacturerOnboarding.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import backend from "../../../api";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, easeOut, AnimatePresence } from "framer-motion";
import Classes from "./Auth.module.css";
import { uploadMedia } from "../../../utils/uploadMedia";
import { setUserData } from "../../../redux-store/auth/actions";

const LoadingSpinner = () => (
  <div className="loadingio-spinner-eclipse-nq4q5u6dq7r">
    <div className="ldio-x2uulkbinbj">
      <div></div>
    </div>
  </div>
);

const fadeInLeft = {
  initial: { opacity: 0, x: -10 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -10 },
  transition: { duration: 0.5, ease: easeOut, delay: 0.3 },
};

function ManufacturerOnboarding() {
  const [state] = useState("createProfile");
  const { user } = useSelector((s) => s.auth);
  const userId = user?._id;

  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");       // local number
  const [profilePhoneCode, setProfilePhoneCode] = useState("+1"); // intl code

  const [manufacturerName, setManufacturerName] = useState("");
  const [manufacturerWebsite, setManufacturerWebsite] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const location = useLocation();

  // Prefill name & phone from user
  useEffect(() => {
    if (user) {
      const existingName =
        user.name ||
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        "";
      setProfileName(existingName);

      const existingPhone = user.phone || "";
      let phone = existingPhone || "";
      let code = "+1";

      if (existingPhone && existingPhone.startsWith("+")) {
        const match = existingPhone.match(/^(\+\d{1,4})(.*)$/);
        if (match) {
          code = match[1] || "+1";
          phone = (match[2] || "").trim();
        }
      } else if (existingPhone) {
        phone = existingPhone;
      }

      setProfilePhone(phone);
      setProfilePhoneCode(code);
    }
  }, [user]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview("");
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  // local slug helper (backend will also create/ensure unique)
  const makeSlug = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "") // remove spaces & symbols
      .slice(0, 60);

  const createOnboarding = async ({
    userId,
    companyName,
    phone,
    websiteURL,
    logo,
  }) => {
    const slug = makeSlug(companyName);

    const body = {
      userId,
      companyName,
      slug, // backend will confirm/adjust uniqueness
      phone,
      websiteURL,
      logo,
    };

    const response = await backend.post(`/api/onboarding/`, body);
    // backend returns { ok: true, data: createdDoc }
    if (
      response?.status === 200 &&
      response?.data?.ok &&
      response?.data?.data?._id
    ) {
      return response.data.data._id;
    }
    return null;
  };

  const updateUser = async (companyId, name, phone) => {
    const trimmedName = (name || "").trim();
    let firstName = "";
    let lastName = "";

    if (trimmedName) {
      const parts = trimmedName.split(" ");
      firstName = parts[0] || "";
      lastName = parts.slice(1).join(" ").trim();
    }

    const body = {
      onboarded: true,
      companyId,
      name: trimmedName || "User",
      firstName,
      lastName,
      phone,
      role: "manufacturer",
    };

    const response = await backend.put(`/api/user/${userId}`, body);
    if (response?.data?.data) {
      await dispatch(setUserData(response.data.data));
    }
  };

  const onFinishProfile = async (e) => {
    e.preventDefault();
    if (!userId) {
      alert("Please sign in again to continue.");
      return;
    }

    // ✅ Frontend required validation with alerts
    const missing = [];
    if (!logoFile) missing.push("company logo");
    if (!profileName.trim()) missing.push("name");
    if (!profilePhoneCode.trim()) missing.push("phone country code");
    if (!profilePhone.trim()) missing.push("phone number");
    if (!manufacturerName.trim()) missing.push("manufacturer name");
    if (!manufacturerWebsite.trim()) missing.push("manufacturer website");

    if (missing.length > 0) {
      alert(
        `Please fill in the following required fields:\n- ${missing.join(
          "\n- "
        )}`
      );
      return;
    }

    setLoading(true);
    try {
      // build full phone with intl code (same pattern as other screens)
      let code = profilePhoneCode || "";
      let local = profilePhone || "";

      if (code) {
        if (!code.startsWith("+")) {
          code = "+" + code.replace(/^\+*/, "");
        }
        code = code.replace(/[^\d+]/g, "");
      }

      local = local.replace(/\s|-/g, "");

      const rawFullPhone = `${code}${local}`;
      const fullPhone = rawFullPhone.replace(/\s|-/g, "");

      // Upload logo (supports array/URL responses)
      let logoUrl = null;
      if (logoFile) {
        const out = await uploadMedia([logoFile]);
        logoUrl = Array.isArray(out) ? out[0] : out;
      }

      const companyId = await createOnboarding({
        userId,
        companyName: manufacturerName,
        phone: fullPhone,
        websiteURL: manufacturerWebsite,
        logo: logoUrl,
      });

      if (companyId) {
        await updateUser(companyId, profileName, fullPhone);
        if (location?.state?.data === "toTemplate") {
          navigateTo(`/templates`);
        } else {
          navigateTo("/listings");
        }
      } else {
        alert(
          "Could not create company profile. Please check the company name and try again."
        );
      }
    } catch (error) {
      console.error(error);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "An error occurred while finishing profile setup. Please try again.";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={Classes.signUp}>
        {/* LEFT SIDE */}
        <div className={Classes.signUpGridLeft}>
          <AnimatePresence mode="wait">
            {state === "createProfile" && (
              <motion.div key="create-profile" {...fadeInLeft} style={{ width: '100%' }}>
                <form
                  onSubmit={onFinishProfile}
                  className={Classes.signUpInnerCon}
                  style={{ margin: '0 auto' }}
                >
                  <a
                    href="/"
                    className={Classes.miniLogo2}
                    style={{ textDecoration: "none" }}
                  >
                    <div className="logo">
                      <img src="/assets/images/logo.png" alt="logo" />
                    </div>
                  </a>

                  <h5 className={Classes.heading2}>Create Your Profile</h5>
                  <p
                    className={Classes.subtitle}
                    style={{ textAlign: "start" }}
                  >
                    Don’t worry – you can edit this later in your settings
                  </p>

                  {/* Upload company logo */}
                  <div style={{ marginBottom: 12 }}>
                    <label
                      className={Classes.fieldLabel}
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      Upload your company logo
                    </label>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 12,
                          border: "1px dashed #d9dee7",
                          display: "grid",
                          placeItems: "center",
                          background: "#f7f9fc",
                          overflow: "hidden",
                        }}
                      >
                        {logoPreview ? (
                          <img
                            src={logoPreview}
                            alt="logo preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: "50%",
                              background: "#e9eef6",
                              display: "inline-block",
                            }}
                          />
                        )}
                      </div>

                      <div>
                        <input
                          id="companyLogoInput"
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setLogoFile(file);
                          }}
                        />
                        <label
                          htmlFor="companyLogoInput"
                          className={Classes.linkButton}
                          style={{
                            cursor: "pointer",
                            padding: "8px 12px",
                            borderRadius: 8,
                            border: "1px solid #e3e9f2",
                            background: "#fff",
                            fontWeight: 700,
                          }}
                        >
                          Choose Image
                        </label>
                        <div
                          className={Classes.miniText2}
                          style={{ marginTop: 6 }}
                        >
                          PNG/JPG, up to ~5MB
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Name */}
                  <input
                    type="text"
                    required
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Enter your name"
                    className={`${Classes.inputField} ${Classes.mb20}`}
                    style={{ width: "100%" }}
                  />

                  {/* Phone: code + number */}
                  <div
                    className={Classes.mb20}
                    style={{ display: "flex", gap: 12 }}
                  >
                    <input
                      type="text"
                      required
                      value={profilePhoneCode}
                      onChange={(e) => {
                        let v = e.target.value || "";

                        // Ensure single "+" at start when something typed
                        if (v.length > 0 && !v.startsWith("+")) {
                          v = "+" + v.replace(/^\+*/, "");
                        }

                        // Allow only + and digits
                        v = v.replace(/[^\d+]/g, "");

                        setProfilePhoneCode(v);
                      }}
                      placeholder="+1"
                      className={Classes.inputField}
                      style={{ maxWidth: "110px" }}
                    />

                    <input
                      type="tel"
                      required
                      value={profilePhone}
                      onChange={(e) => {
                        let v = e.target.value || "";
                        // Allow digits, spaces and dashes in UI
                        v = v.replace(/[^\d\s-]/g, "");
                        setProfilePhone(v);
                      }}
                      placeholder="Enter your phone number"
                      className={Classes.inputField}
                      style={{ width: "100%" }}
                    />
                  </div>

                  {/* Manufacturer Name */}
                  <input
                    type="text"
                    required
                    value={manufacturerName}
                    onChange={(e) => setManufacturerName(e.target.value)}
                    placeholder="Enter your manufacturer name"
                    className={`${Classes.inputField} ${Classes.mb20}`}
                    style={{ width: "100%" }}
                  />

                  {/* Manufacturer Website */}
                  <input
                    type="url"
                    required
                    value={manufacturerWebsite}
                    onChange={(e) => setManufacturerWebsite(e.target.value)}
                    placeholder="Enter your manufacturer website"
                    className={`${Classes.inputField} ${Classes.mb20}`}
                    style={{ width: "100%" }}
                  />

                  <button
                    type="submit"
                    className={`${Classes.button} ${Classes.signInButton}`}
                    disabled={loading}
                  >
                    Finish
                    {loading && <LoadingSpinner />}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT SIDE */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: easeOut }}
          className={Classes.signUpGridRight}
        >
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: easeOut, delay: 0.3 }}
            className={Classes.signUpInnerCon2}
          >
            <h5 className={Classes.title3}>Why Partner With Us</h5>
            <br />
            <div className={Classes.infoElem}>
              <div className={Classes.infoImgBox}>
                <img
                  src="/assets/icons/signup1.png"
                  draggable={false}
                  alt=""
                />
              </div>
              <div className={Classes.infoElemRight}>
                <h5 className={Classes.title5}>Global Exposure</h5>
                <h5 className={Classes.miniText2}>
                  Feature your lighting products to a worldwide network of
                  architects, designers, and industry professionals.
                </h5>
              </div>
            </div>
            <div className={Classes.infoElem}>
              <div className={Classes.infoImgBox}>
                <img
                  src="/assets/icons/signup2.png"
                  draggable={false}
                  alt=""
                />
              </div>
              <div className={Classes.infoElemRight}>
                <h5 className={Classes.title5}>Data-Driven Insights</h5>
                <h5 className={Classes.miniText2}>
                  Access detailed analytics on product views, clicks, downloads,
                  and customer engagement trends.
                </h5>
              </div>
            </div>
            <div className={Classes.infoElem}>
              <div className={Classes.infoImgBox}>
                <img
                  src="/assets/icons/signup3.png"
                  draggable={false}
                  alt=""
                />
              </div>
              <div className={Classes.infoElemRight}>
                <h5 className={Classes.title5}>Effortless Upload Process</h5>
                <h5 className={Classes.miniText2}>
                  Feature your lighting products to a worldwide network of
                  architects, designers, and industry professionals.
                </h5>
              </div>
            </div>
            <div className={Classes.infoElem}>
              <div className={Classes.infoImgBox}>
                <img
                  src="/assets/icons/signup4.png"
                  draggable={false}
                  alt=""
                />
              </div>
              <div className={Classes.infoElemRight}>
                <h5 className={Classes.title5}>Showcase Your Brand</h5>
                <h5 className={Classes.miniText2}>
                  Maintain and display your entire product line in one
                  professional, searchable profile.
                </h5>
              </div>
            </div>
          </motion.div>
          <img
            src="/assets/images/auth-bg.png"
            alt=""
            aria-hidden="true"
            className={Classes.authBg}
            draggable={false}
          />
        </motion.div>
      </div>
    </>
  );
}

export default ManufacturerOnboarding;
